import { z } from "zod";
import { marcacaoFacialSchema } from "./face/marcacao";
import { MODELOS_DA_FACE } from "./face/vistas";

export const STATUS_DO_PLANO = ["RASCUNHO", "APRESENTADO", "APROVADO", "RECUSADO", "CONCLUIDO", "CANCELADO"] as const;
export type StatusDoPlano = (typeof STATUS_DO_PLANO)[number];

export const ROTULO_DO_STATUS_DO_PLANO: Record<StatusDoPlano, string> = {
  RASCUNHO: "Rascunho",
  APRESENTADO: "Apresentado",
  APROVADO: "Aprovado",
  RECUSADO: "Recusado",
  CONCLUIDO: "Concluído",
  CANCELADO: "Cancelado",
};

/** Itens (procedimento, dentes, preço) só mudam antes da aprovação — aprovado, o preço é o combinado. */
export const podeEditarItens = (status: StatusDoPlano) => status === "RASCUNHO" || status === "APRESENTADO";

/** Para onde cada situação pode ir — a tela mostra só os botões válidos e o servidor confere.
 *  "Apresentado" saiu do fluxo (a entrega fica em `entregueEm`, marcada ao imprimir); continua no
 *  enum só pra plano antigo, que segue como rascunho. */
export const PROXIMOS_STATUS: Record<StatusDoPlano, StatusDoPlano[]> = {
  RASCUNHO: ["APROVADO", "RECUSADO", "CANCELADO"],
  APRESENTADO: ["APROVADO", "RECUSADO", "CANCELADO"],
  APROVADO: ["CONCLUIDO", "CANCELADO"],
  RECUSADO: ["RASCUNHO"],
  CONCLUIDO: ["APROVADO"],
  CANCELADO: ["RASCUNHO"],
};

const centavos = z.number().int().min(0).max(100_000_000);

export const criarPlanoSchema = z.object({
  patientId: z.string().min(1),
  titulo: z.string().trim().max(120).optional(),
  dentistaId: z.string().min(1).nullish(),
});

export const atualizarPlanoSchema = z.object({
  id: z.string().min(1),
  titulo: z.string().trim().max(120).nullish(),
  dentistaId: z.string().min(1).nullish(),
  descontoCents: centavos.optional(),
  observacoes: z.string().trim().max(2000).nullish(),
  validadeAte: z.coerce.date().nullish(),
  /** Desenho do modo Face; null volta a seguir o sexo do paciente. */
  modeloDaFace: z.enum(MODELOS_DA_FACE).nullish(),
});

export const mudarStatusDoPlanoSchema = z.object({ id: z.string().min(1), status: z.enum(STATUS_DO_PLANO) });

export const salvarItemDoPlanoSchema = z
  .object({
    planoId: z.string().min(1),
    itemId: z.string().min(1).optional(),
    offeredServiceId: z.string().min(1).nullish(),
    nome: z.string().trim().min(2, "Diga o procedimento").max(160),
    dentes: z.string().trim().max(120).nullish(),
    precoCents: centavos.nullish(),
    /** Cobrado por dente: `precoCents` é unitário e o total do item é preço × quantidade. */
    porDente: z.boolean().default(false),
    quantidade: z.number().int().min(1).max(64).default(1),
    /** Marcações do modo Face — substituem as do item. Ausente = não mexe. */
    marcacoes: z.array(marcacaoFacialSchema).max(60).optional(),
  });

export const reordenarItensSchema = z.object({ planoId: z.string().min(1), ids: z.array(z.string().min(1)).min(1).max(200) });

export const guardarPrecoSchema = z.object({ serviceId: z.string().min(1), precoCents: centavos.min(1) });

export type ItemParaTotal = { precoCents: number | null; quantidade?: number | null };

/** Total do item: preço unitário × quantidade (1 quando não é por dente). */
export const totalDoItem = (i: ItemParaTotal) => (i.precoCents == null ? null : i.precoCents * Math.max(i.quantidade ?? 1, 1));

/** Subtotal, desconto (nunca maior que o subtotal), total e quantos itens estão sem preço. */
export function totaisDoPlano(itens: ItemParaTotal[], descontoCents = 0) {
  const subtotal = itens.reduce((t, i) => t + (totalDoItem(i) ?? 0), 0);
  const desconto = Math.min(Math.max(descontoCents, 0), subtotal);
  return { subtotal, desconto, total: subtotal - desconto, semPreco: itens.filter((i) => i.precoCents == null).length };
}

/**
 * O preço do plano vai pro catálogo sozinho só quando o procedimento ainda não tem preço (0 =
 * sem preço). Trocar um preço existente é decisão de quem edita preços — pede o PIN à parte.
 */
export function deveGuardarNoCatalogo(precoDoCatalogo: number | null | undefined, precoDoItem: number | null | undefined) {
  return !precoDoCatalogo && !!precoDoItem && precoDoItem > 0;
}
